import { memo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { extractErrorMessage } from 'app/providers/with-wagmi-auth/utils';
import { paymentModel } from 'entities/payment';
import { selectAllPaymentMethods } from 'entities/payment/model';
import {
  COLOR_SECONDARY,
  getToastMessage,
  Icons,
  TokenAvatar,
  useAppDispatch,
  useShallowSelector,
  useStepper,
} from 'shared';

import { STEPPER_ROUTES } from '../../lib/stepper';
import { BackgroundCard, Debit, Onramper } from '../shared';

import { PaymentButton } from './payment-button';

const OnRamperIcon = () => {
  return (
    <Stack direction="row" alignItems="center" gap={1.5}>
      <Typography variant="body1" className="secondary">
        via Onramper
      </Typography>
      <Box component="img" alt="on-ramp" src={Onramper} sx={{ width: 35, height: 30, objectFit: 'contain' }} />
    </Stack>
  );
};

export const PaymentMethodsView = memo(() => {
  const dispatch = useAppDispatch();
  const { next, goBack } = useStepper();
  const paymentMethods = useShallowSelector(selectAllPaymentMethods);

  const estimate = async () => {
    try {
      await dispatch(paymentModel.thunks.estimatePaymentMethod()).unwrap();
      return true;
    } catch (e) {
      getToastMessage('error', extractErrorMessage(e));
      return false;
    }
  };

  const selectCryptoMethod = async (tokenId: string) => {
    const success = await estimate();

    if (success) {
      dispatch(paymentModel.actions.setPaymentTokenId(tokenId));
      next(STEPPER_ROUTES.PAYMENT_CRYPTO_CONFIRMATION);
    }
  };

  const selectDebitMethod = async () => {
    const success = await estimate();

    if (success) next(STEPPER_ROUTES.PAYMENT_DEBIT_CONFIRMATION);
  };

  return (
    <BackgroundCard title="Payment methods" onBack={goBack}>
      <Stack gap={1} width="100%">
        {paymentMethods.map(({ networkImage, networkName, tokenName, tokenId, tokenImage }, idx) => (
          <PaymentButton
            key={tokenId}
            onClick={() => selectCryptoMethod(tokenId)}
            endIcon={<Icons.ArrowChevron sx={{ color: COLOR_SECONDARY }} />}
          >
            <TokenAvatar
              tokenName={tokenName}
              networkImg={networkImage}
              tokenImg={tokenImage}
              networkName={networkName}
            />
            <Typography variant="body1" textTransform="capitalize" className="primary">
              {tokenName}
            </Typography>
          </PaymentButton>
        ))}
        <PaymentButton onClick={selectDebitMethod} endIcon={<OnRamperIcon />}>
          <Box component="img" alt="debit" src={Debit} sx={{ width: 35, height: 30, objectFit: 'contain' }} />
          <Typography variant="body1" textTransform="capitalize" className="primary">
            Debit
          </Typography>
        </PaymentButton>
      </Stack>
    </BackgroundCard>
  );
});
